/**
 * ructec-Bulli drive (user 2026-10-05: "mit diesem Bulli fahren … gefedert … Kurven und Kreuzungen … über eine Rampe …
 * immer schneller … in die Federn gehen und fast zum Kippen neigen"). Decisions of the user: always once at about
 * 1000 m; the VW emblem replaced by the ructec badge; logos, roof rack with timber.
 *
 * Third version (user 2026-10-05: "keine Hindernisse … aus dem Bulli rausfallen soll nicht sein … die Fahrt endet fix
 * … das andere Auto überholt einen von hinten und man muss es wegdrängen – eine echte Verfolgung mit Rempeln, ab und zu
 * Gegenverkehr … Kamera weiter hinten … Fahrwerk extrem weich … überladen, bei jedem seitlichen Crash (inkl. Geräusch)
 * verliert er was … die Fahrt dauert 1000 m und wird immer schneller, danach ist die Spielgeschwindigkeit wie normal
 * nach 2000 m"; later "die Länge nur mit 500 Metern"): a fixed ride of rideLength m (≈ 1000 → 1500 m of the run, the
 * runner hops out at the end and runs on at the run's own speed there), no obstacles, no way out of the van. A rival (RivalConfig) chases it – block him, shove him
 * off; oncoming traffic now and then. The roof is overloaded (CargoConfig): every side crash loses material.
 *
 * Track space: x right, y up, distance along the track (m). Speeds in m/s, times in s, angles in degrees.
 */
export interface BusConfig {
  /** Track meters the van drives from where the runner gets in (the ride ends there with a hop out – always). */
  readonly rideLength: number;
  /** Behind the end the track stays calm a little longer: the runner lands on open road (LevelGenerator ride window). */
  readonly runOut: number;
  /** Forward speed = run speed × this, rising with the meters driven ("immer schneller"). */
  readonly speedFactor: { readonly start: number; readonly end: number };
  /** Seconds per lane while steering – a van changes lanes a little slower than the runner. */
  readonly laneChangeDuration: number;
  /** Gameplay box of the van around the driver's seat (width, height, extent behind and ahead of the seat). */
  readonly body: { readonly width: number; readonly height: number; readonly rear: number; readonly front: number };
  /** Driver's seat in vehicle space (from the ground centre of the van, +z forward). */
  readonly seat: { readonly y: number; readonly z: number };
  /** The runner leaves the van only at the end – should anything else end the ride, it is thrown this way. */
  readonly throwOff: { readonly ahead: number; readonly speed: number };
  /**
   * Pushed off the edge of the road by the rival the van tips over (user: "der eigene Bulli fällt um, dann blinkt er und
   * kommt wieder ins Spiel – man verliert Flaschen"): penalty bottles and cargo pieces, then seconds blinking
   * (no crash counts).
   */
  readonly tip: { readonly penalty: number; readonly cargo: number; readonly seconds: number };
  /** Ramp landing: lateral speed (m/s) at touch-down for a clean / slanted landing; above: very slanted (two wheels). */
  readonly landing: { readonly cleanBelow: number; readonly slantedBelow: number };
  readonly cargo: CargoConfig;
  readonly traffic: TrafficConfig;
  /** Gravity of the airborne van (m/s²) – the runner's own jump gravity would let it float. */
  readonly gravity: number;
  /** End of the ride: hop out at this vertical speed. */
  readonly hopOutSpeed: number;
  /** The empty van brakes at this rate (m/s²) once the runner is out. */
  readonly brakeDeceleration: number;
  readonly presentation: BusPresentationConfig;
}

/** The (over)load on the roof rack – what the van delivers. Losing it never ends the ride. */
export interface CargoConfig {
  /** Pieces on the rack at the start (VEH_BUS_A_PIVOT_CARGO_1 … n, the highest number – the top – goes first). */
  readonly pieces: number;
  /** Bottles per piece that arrives at the end of the ride. */
  readonly bonusPerPiece: number;
  /**
   * Pieces a crash costs: head-on (oncoming traffic) / side (the rival – also when the van shoves him) / rammed while the
   * van is in the air after a ramp (user 2026-10-05: "in der Luft gerammt … verliert man viel Material").
   */
  readonly loss: { readonly headOn: number; readonly side: number; readonly airborne: number };
  /** Pieces a ramp landing costs: clean nothing, slanted (steering while touching down) / very slanted (two wheels). */
  readonly landing: { readonly slanted: number; readonly rough: number };
  /** After a crash the next one counts only after this many seconds (one contact, one loss). */
  readonly grace: number;
}

export const TrafficRole = {
  /** Oncoming traffic on the left lane (lane 0). */
  Oncoming: 'ONCOMING',
  /** The rival chasing the van (RivalConfig). */
  Rival: 'RIVAL',
} as const;
export type TrafficRole = (typeof TrafficRole)[keyof typeof TrafficRole];

/** The traffic is the same Bulli as the van (user 2026-10-05) – VEH_BUS_B, painted per car. */
export type TrafficKind = 'bulli';

export interface TrafficKindConfig {
  readonly assetId: string;
  /** Gameplay box (m) – a little narrower than the model, so neighbouring lanes pass. */
  readonly width: number;
  readonly length: number;
  readonly height: number;
  /** Paint colours of the model's MAT_*_PAINT: the first is the rival's, oncoming cars pick one of the others. */
  readonly paints: readonly string[];
}

export type Weighted<T extends string> = readonly (readonly [T, number])[];

/**
 * The rival – a craftsman in a hurry who fights the van for the road (user 2026-10-05: "wesentlich aggressiver … ein echter
 * Kampf um den Platz"): he comes from behind on a lane beside it (the HUD warns), drives alongside, creeps ahead and
 * attacks again and again – he swings over and PRESSES against the van. Steer against him (into him) and he takes a hit
 * and staggers; after `toughness` hits he flies off the road. Let him press and he shoves the van a lane over – at the
 * edge of the road it tips over (BusConfig.tip). Blocked behind the van he rams its tail and tries the other side. Once
 * past he cuts in and brake-checks, then drives off (penalty). The next one comes 1–2 s after.
 */
export interface RivalConfig {
  readonly kind: TrafficKind;
  /** Index into the kind's paints – one colour, so the rival is recognised (red). */
  readonly paint: number;
  /** Seconds until the first rival (after the calm start) and between two. */
  readonly firstAfter: number;
  readonly between: readonly [number, number];
  /** Spawned this far behind the van, approaching this much faster (m/s). */
  readonly behind: number;
  readonly approach: number;
  /** Alongside he creeps ahead this much faster than the van (m/s). */
  readonly creep: number;
  /**
   * Attacks alongside: every … s he swings over at `leanRate` m/s and presses against the van for `hold` s; pressing
   * shoves the van a lane over after `shoveAfter` s of contact.
   */
  readonly attack: { readonly every: readonly [number, number]; readonly leanRate: number; readonly hold: number; readonly shoveAfter: number };
  /** Hits (steered into) he takes before he flies off the road; a hit staggers him away for `stagger` s, `slow` m/s slower. */
  readonly toughness: number;
  readonly stagger: { readonly seconds: number; readonly slow: number; readonly away: number };
  /** Blocked behind the van in its lane: he rams its tail, after `switchAfter` s he tries the other side. */
  readonly switchAfter: number;
  /** He is past once his tail is this far ahead of the van's nose (m): he cuts in, brakes to `brakeTo` × the van's speed for `brakeSeconds`, then drives off this much faster. */
  readonly passedAhead: number;
  readonly passBrake: { readonly to: number; readonly seconds: number };
  readonly leaveFaster: number;
  /** The van steers into him (instead of being pressed) when it moves sideways towards him at least this fast (m/s). */
  readonly shoveLateral: number;
  /** Bottles for driving him off the road (or him crashing into oncoming traffic) / lost when he gets past. */
  readonly shoveBonus: number;
  readonly passPenalty: number;
}
export interface TrafficConfig {
  readonly kinds: Readonly<Record<TrafficKind, TrafficKindConfig>>;
  /** Oncoming traffic on lane 0, now and then. */
  readonly oncoming: {
    /** Seconds between two cars at the start and at the end of the ride. */
    readonly every: readonly [number, number];
    readonly kinds: Weighted<TrafficKind>;
    readonly speed: readonly [number, number];
    /** Spawned this far ahead of the van (never behind the next crossing). */
    readonly ahead: number;
    /**
     * It goes for the van (user: "dass man spürt, dass der einen crashen will"): `from` m ahead it swerves into the
     * van's lane, at `lunge` m it follows the van once more – then it is committed (dodge late!). Lane changes take
     * `laneChange` s.
     */
    readonly hunt: { readonly from: number; readonly lunge: number; readonly laneChange: number };
  };
  readonly rival: RivalConfig;
  /** No new traffic in the first / last meters of the ride (getting in, the finale). */
  readonly calmStart: number;
  readonly calmEnd: number;
  /** Seconds per lane change of a car, the gap it keeps behind a slower car (m). */
  readonly laneChangeDuration: number;
  readonly followGap: number;
  /** A new oncoming car must leave the van a free lane around the moment it meets it (± m). */
  readonly escapeWindow: number;
  /** Cars further than this behind / ahead of the van are taken out. */
  readonly despawnBehind: number;
  readonly despawnAhead: number;
  /** HUD warning: the rival reaches the van within this many seconds. */
  readonly warnSeconds: number;
  /** A crashed car spins off: lateral push (m/s), spin (deg/s), how fast it stops (m/s²). */
  readonly wreck: { readonly push: number; readonly spin: number; readonly friction: number };
  /** Gravity of a car off a ramp lip (m/s²). */
  readonly gravity: number;
}

export interface BusPresentationConfig {
  /** Sprung body on the wheels: natural frequency (Hz) and damping ratio of roll, pitch and heave (spring-damper). */
  readonly suspension: { readonly hz: number; readonly damping: number };
  /** Body roll per lateral acceleration (deg per m/s²) and its limit – beyond `tipFrom` the inner wheels lift. */
  readonly roll: { readonly perAccel: number; readonly max: number; readonly tipFrom: number; readonly tipLiftMax: number };
  /** Pitch per forward acceleration (deg per m/s²): squats when it speeds up, dives when it lands. */
  readonly pitch: { readonly perAccel: number; readonly max: number; readonly landingDip: number };
  /** Heave: the body sinks this far per m/s of landing speed (m). */
  readonly landingSink: number;
  /** A side crash throws the body over (roll speed, deg/s) away from the hit. */
  readonly crashRoll: number;
  /** Steering angle of the front wheels per lateral speed (deg per m/s) and its limit. */
  readonly steer: { readonly perLateral: number; readonly max: number };
  /** Yaw of the whole van into a lane change (deg per m/s of lateral speed, limit) – it points where it goes. */
  readonly yaw: { readonly perLateral: number; readonly max: number };
  /** Road rumble of the body (m, Hz) – more at speed. */
  readonly rumble: { readonly amplitude: number; readonly hz: number };
  /** Wheel radius (m) – the wheels turn with the distance driven. */
  readonly wheelRadius: number;
  /** Toasts (s). */
  readonly toastSeconds: number;
  /** Camera shake of a crash / a rough landing / a clean landing / a piece of load lost. */
  readonly trauma: { readonly crash: number; readonly rough: number; readonly landing: number; readonly cargo: number };
  /** A lost piece of load: thrown back and up off the rack (m/s), sideways spread, spin (deg/s). */
  readonly cargoThrow: { readonly back: number; readonly up: number; readonly side: number; readonly spin: number };
  /** Blinker frequency of the traffic (Hz). */
  readonly blinkHz: number;
}

export const BUS_CONFIG: BusConfig = {
  // 500 m (user 2026-10-05: "die Länge nur mit 500 Metern"), from ≈ 8000 m of the run (SEG_BUS_START_40M)
  rideLength: 500,
  runOut: 80,
  // from 8000 m the run is at its top speed (16 m/s): 16 → 20 m/s (58 → 72 km/h) – the pace the fight was tuned at
  speedFactor: { start: 1, end: 1.25 },
  laneChangeDuration: 0.38,
  // VEH_BUS_A: 1.75 m wide (without mirrors), 2.4 m high with the overloaded rack, 4.3 m long; the driver sits 1.4 m behind the nose
  body: { width: 1.7, height: 2.4, rear: 2.75, front: 1.45 },
  seat: { y: 0.95, z: 0.7 },
  throwOff: { ahead: 3, speed: 5 },
  tip: { penalty: 15, cargo: 2, seconds: 2.2 },
  // a lane change of the van peaks at ≈ 2 × 2.2 m / 0.38 s ≈ 11.6 m/s – landing during it is very slanted
  landing: { cleanBelow: 1.5, slantedBelow: 6 },
  cargo: {
    // overloaded (user): four layers of bundles
    pieces: 16,
    bonusPerPiece: 4,
    loss: { headOn: 3, side: 1, airborne: 4 },
    landing: { slanted: 1, rough: 2 },
    grace: 0.8,
  },
  traffic: {
    kinds: {
      // VEH_BUS_B: the same van as the ructec-Bulli (1.75 × 2.3 × 4.3 m), red for the rival, other colours oncoming
      bulli: { assetId: 'VEH_BUS_B', width: 1.7, length: 4.2, height: 2.3, paints: ['#c8231e', '#f2f2ee', '#f2b705', '#2e7d32', '#7fb3d5', '#e8712a'] },
    },
    // now and then (user: "manchmal kommt ein anderer Bulli entgegen")
    oncoming: { every: [14, 9], kinds: [['bulli', 1]], speed: [10, 13], ahead: 150, hunt: { from: 80, lunge: 46, laneChange: 0.7 } },
    rival: {
      kind: 'bulli',
      paint: 0,
      firstAfter: 2,
      // the next one right after (user: "innerhalb von ein, zwei Sekunden")
      between: [1, 2],
      behind: 30,
      approach: 8,
      creep: 1.6,
      attack: { every: [0.6, 1.2], leanRate: 4, hold: 1.2, shoveAfter: 0.8 },
      // two, three times steered against before he flies off (user)
      toughness: 3,
      stagger: { seconds: 0.6, slow: 3, away: 0.7 },
      switchAfter: 0.6,
      passedAhead: 4,
      passBrake: { to: 0.75, seconds: 1.4 },
      leaveFaster: 7,
      shoveLateral: 1.5,
      shoveBonus: 15,
      passPenalty: 10,
    },
    calmStart: 50,
    calmEnd: 110,
    laneChangeDuration: 0.9,
    followGap: 8,
    escapeWindow: 9,
    despawnBehind: 70,
    despawnAhead: 230,
    warnSeconds: 2.4,
    wreck: { push: 5, spin: 300, friction: 9 },
    gravity: 11,
  },
  gravity: 11,
  hopOutSpeed: 6,
  brakeDeceleration: 9,
  presentation: {
    // extremely soft (user): ≈ 0.85 Hz and little damping – it wallows, rolls far out of every lane change and bounces
    suspension: { hz: 0.85, damping: 0.28 },
    roll: { perAccel: 0.9, max: 13, tipFrom: 9, tipLiftMax: 5 },
    pitch: { perAccel: 0.55, max: 7, landingDip: 8 },
    landingSink: 0.07,
    crashRoll: 60,
    steer: { perLateral: 3.2, max: 22 },
    yaw: { perLateral: 0.9, max: 7 },
    rumble: { amplitude: 0.01, hz: 9 },
    wheelRadius: 0.3275,
    toastSeconds: 1.8,
    trauma: { crash: 0.5, rough: 0.35, landing: 0.15, cargo: 0.15 },
    cargoThrow: { back: 4, up: 3.5, side: 1.6, spin: 260 },
    blinkHz: 2.6,
  },
};
